import React, { useCallback, useMemo, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TablePagination,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { usePermissions } from '../../../utils/usePermissions';
import { EditEmptyState } from '../reports/edit/EditCommon';
import ReportHistoryButton from '../reports/history/ReportHistoryButton';
import { ACTION_BUTTON_SX } from '../reports/summary/summaryStyles';
import { PRIMARY_BUTTON_SX } from './mathTableStyles';
import MathReportGrid from './report/MathReportGrid';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, filterRows, getMonthsList } from './report/mathReportCalc';
import { useMathReport } from './report/useMathReport';
import type { MathBusyAction } from './report/useMathReport';

const BORDER = '1px solid #e2e8f0';

const BUSY_TEXT: Record<MathBusyAction, string> = {
  create: 'Создаются записи за месяц',
  reindex: 'Выполняется перепроверка записей',
  recalculate: 'Выполняется пересчет остатков'
};

const CurrentMathReport: React.FC = () => {
  const { hasPerm } = usePermissions();
  const canEditCells = hasPerm('reports.math.report.edit_cells');
  const canCheckStatus = hasPerm('reports.math.report.check_status');
  const canCreate = hasPerm('reports.math.report.create');
  const canReindex = hasPerm('reports.math.report.reindex');
  const canRecalculate = hasPerm('reports.math.report.recalculate');
  const canDownload = hasPerm('reports.math.report.download');

  const {
    month,
    setMonth,
    columns,
    rows,
    loading,
    error,
    retry,
    busy,
    downloadingId,
    saveCell,
    toggleStatus,
    download,
    createRecords,
    reindex,
    recalculate
  } = useMathReport();

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);

  const months = useMemo(getMonthsList, []);
  const filtered = useMemo(() => (rows ? filterRows(rows, query) : []), [rows, query]);

  // Страница не выходит за последнюю, даже если строк стало меньше (поиск, перезагрузка)
  const lastPage = Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1);
  const safePage = Math.min(page, lastPage);
  const pageRows = useMemo(
    () => filtered.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage),
    [filtered, safePage, rowsPerPage]
  );

  const handleMonthChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      setMonth(event.target.value);
      setPage(0);
    },
    [setMonth]
  );

  const handleQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setPage(0);
  }, []);

  const handlePageChange = useCallback((_event: unknown, next: number) => setPage(next), []);

  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const renderContent = () => {
    if (error && !rows) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Не удалось загрузить мат. отчет"
          text={error}
          action={<Button variant="outlined" onClick={retry} sx={ACTION_BUTTON_SX}>Повторить</Button>}
        />
      );
    }
    if (!rows || !columns) {
      return (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }
    if (rows.length === 0) {
      return (
        <EditEmptyState
          icon={<InboxOutlinedIcon />}
          title="Записей за выбранный месяц нет"
          text={canCreate ? 'Создайте записи по аптекам, чтобы начать заполнение мат. отчета.' : undefined}
          action={
            canCreate ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={createRecords} disabled={!!busy} sx={PRIMARY_BUTTON_SX}>
                Создать записи
              </Button>
            ) : undefined
          }
        />
      );
    }
    if (columns.length === 0) {
      return (
        <EditEmptyState
          icon={<TableChartOutlinedIcon />}
          title="Колонки мат. отчета не настроены"
          text="Добавьте поля на вкладке «Отображение», чтобы таблица появилась."
        />
      );
    }
    if (filtered.length === 0) {
      return (
        <EditEmptyState
          icon={<SearchOffIcon />}
          title="Ничего не найдено"
          text="Аптеки с таким номером в этом месяце нет. Проверьте запрос."
        />
      );
    }
    return (
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          border: BORDER,
          borderRadius: '12px',
          overflow: 'hidden',
          transition: 'opacity 0.15s ease'
        }}
        style={{ opacity: loading ? 0.55 : 1 }}
      >
        <MathReportGrid
          rows={pageRows}
          columns={columns}
          canEditCells={canEditCells}
          canCheckStatus={canCheckStatus}
          canDownload={canDownload}
          downloadingId={downloadingId}
          scrollKey={`${month}|${safePage}|${rowsPerPage}|${query}`}
          onToggleStatus={toggleStatus}
          onSaveCell={saveCell}
          onDownload={download}
        />
        <TablePagination
          component="div"
          showFirstButton
          showLastButton
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={safePage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
          sx={{ flexShrink: 0, borderTop: BORDER }}
        />
      </Paper>
    );
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Backdrop
        open={!!busy}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          flexDirection: 'column',
          gap: 2,
          color: '#1e293b',
          backgroundColor: 'rgba(248, 250, 252, 0.8)'
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>{busy ? `${BUSY_TEXT[busy]}, подождите` : ''}</Typography>
      </Backdrop>

      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Select
            size="small"
            value={month}
            onChange={handleMonthChange}
            disabled={!!busy}
            sx={{ minWidth: 200, backgroundColor: '#ffffff', borderRadius: '8px' }}
          >
            {months.map((item) => (
              <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
            ))}
          </Select>

          <TextField
            size="small"
            placeholder="Поиск по номеру аптеки"
            value={query}
            onChange={handleQueryChange}
            disabled={!!busy}
            sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff', borderRadius: '8px' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                </InputAdornment>
              )
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <ReportHistoryButton screen="math" period={month} />
          {canReindex && (
            <Tooltip title="Добавить недостающие записи мат. отчета за выбранный месяц" enterDelay={800} arrow>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<FactCheckOutlinedIcon />}
                  onClick={reindex}
                  disabled={!!busy || !rows}
                  sx={ACTION_BUTTON_SX}
                >
                  Перепроверка
                </Button>
              </span>
            </Tooltip>
          )}
          {canRecalculate && (
            <Tooltip title="Пересчитать остатки с выбранного месяца" enterDelay={800} arrow>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<CalculateOutlinedIcon />}
                  onClick={recalculate}
                  disabled={!!busy || !rows}
                  sx={ACTION_BUTTON_SX}
                >
                  Пересчет
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {renderContent()}
    </Box>
  );
};

export default CurrentMathReport;
